import { API_URL } from 'config';
import React from 'react';
import { useLanguage } from '../../../context/LanguageContext';

export const LicenseTab = ({ 
  isActivated, 
  hasKey, 
  licenseInfo, 
  modules, 
  triggerToast, 
  handleFetchResponse, 
  fetchLicenseData,
  onBuyKeyClick,
  onUpgradeDemo,
  onCopyKey,
  onUpgradeToPermanent,
  licenseType,
  expiresAt,
  isExpired,
  isBlocked
}) => {
  const { t } = useLanguage();

  const handleResetHWID = async () => {
    if (licenseType === 'demo') {
      triggerToast(t('dashboard.messages.demoResetDisabled'), 'warning');
      return;
    }

    if (!isActivated) {
      triggerToast(t('dashboard.messages.keyNotBound'), 'warning');
      return;
    }

    if (isExpired) {
      triggerToast(t('dashboard.messages.licenseExpired'), 'warning');
      return;
    }

    if (isBlocked) {
      triggerToast(t('dashboard.messages.licenseBlocked'), 'error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/license/reset-hwid`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!handleFetchResponse(response)) return;

      const data = await response.json();

      if (response.status === 200) {
        triggerToast(t('dashboard.messages.hwidResetSuccess'), 'success');
        fetchLicenseData(); 
      } else if (response.status === 429) {
        triggerToast(data.detail || t('dashboard.messages.tooManyRequests'), 'warning');
      } else {
        triggerToast(data.detail || t('dashboard.messages.hwidResetFailed'), 'error');
      }
    } catch (error) {
      triggerToast(t('dashboard.messages.networkError'), 'error');
    }
  };

  const handleDeleteKey = async () => {
    if (!window.confirm(t('dashboard.messages.confirmDeleteKey'))) return;

    try {
      const response = await fetch(`${API_URL}/license/delete-key`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!handleFetchResponse(response)) return;

      const data = await response.json();

      if (response.status === 200) {
        triggerToast(t('dashboard.messages.keyDeletedSuccess'), 'success');
        fetchLicenseData();
      } else {
        triggerToast(data.detail || t('dashboard.messages.keyDeleteFailed'), 'error');
      }
    } catch (error) {
      triggerToast(t('dashboard.messages.networkError'), 'error');
    }
  };

  const handleExtendLicense = async () => {
    try {
      const response = await fetch(`${API_URL}/license/extend-license`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!handleFetchResponse(response)) return;

      const data = await response.json();

      if (response.status === 200 && data.payment_url) {
        triggerToast(t('dashboard.messages.redirectingToPayment'), 'info');
        window.location.href = data.payment_url;
      } else if (response.status === 200) {
        triggerToast(t('dashboard.messages.extensionRequestSent'), 'success');
        fetchLicenseData();
      } else {
        triggerToast(data.detail || t('dashboard.messages.extensionRequestFailed'), 'error');
      }
    } catch (error) {
      triggerToast(t('dashboard.messages.networkError'), 'error');
    }
  };

  const permanentLabels = ['permanent', 'lifetime', 'вечный', 'вічний', '永久'];
  const isPermanent = permanentLabels.some(label =>
    String(licenseType).toLowerCase() === label.toLowerCase()
  );
  const isResetDisabled = licenseType === 'demo' || !isActivated || isExpired || isBlocked;

  const getStatusText = () => {
    if (isBlocked) return t('dashboard.blocked');
    if (isExpired) return t('dashboard.expired');
    if (isActivated) return t('dashboard.activated');
    return t('dashboard.notActivated');
  };

  const getStatusColor = () => {
    if (isBlocked) return '#ef4444';
    if (isExpired) return '#f59e0b';
    if (isActivated) return '#10b981';
    return '#6b7280';
  };

  const getStatusIcon = () => {
    if (isBlocked) return 'fa-lock';
    if (isActivated) return 'fa-lock-open';
    return 'fa-clock';
  };

  const getModuleName = (moduleId) => {
    const moduleData = t(`modules.${moduleId}`, { returnObjects: true });
    return moduleData?.name || moduleId;
  };

  return (
    <div>
      <div className="dash-title-block">
        <h2 className="dash-title">{t('dashboard.title')}</h2>
      </div>

      <div className="main-dashboard-grid">
        <div className="cyber-panel">
          <div className="panel-header">
            <h3>{t('dashboard.keyInfo')}</h3>
            <span style={{ 
              color: getStatusColor(), 
              fontWeight: '700'
            }}>
              <i className={`fas ${getStatusIcon()}`}></i>{' '}
              {getStatusText()}
            </span>
          </div>

          {hasKey && licenseInfo ? (
            <>
              <div className="info-macro-grid" style={{ marginBottom: '20px' }}>
                <div className="macro-box">
                  <label>{t('dashboard.licenseKey')}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{licenseInfo.key}</span>
                    <button
                      className="key-copy-btn"
                      onClick={onCopyKey}
                      title={t('dashboard.copyKey')}
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
                <div className="macro-box">
                  <label>{t('dashboard.licenseType')}</label>
                  <span>{licenseInfo.type}</span>
                </div>
                <div className="macro-box">
                  <label>{t('dashboard.expiresAt')}</label>
                  <span>{['Вечный', 'Lifetime', 'Вічний', '永久', 'permanent'].includes(licenseInfo.expires_at) ? t('dashboard.lifetime') : new Date(licenseInfo.expires_at).toLocaleDateString()}</span>
                </div>
                <div className="macro-box">
                  <label>{t('dashboard.activeModulesCount')}</label>
                  <span>{modules.filter(m => m.owned).length} {t('dashboard.active')}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  className="cyber-action-btn btn-purge" 
                  onClick={handleResetHWID}
                  disabled={isResetDisabled}
                  style={{ 
                    opacity: isResetDisabled ? 0.5 : 1,
                    cursor: isResetDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <i className="fas fa-eraser"></i> {t('dashboard.resetHwid')}
                </button>

                <button 
                  className="cyber-action-btn" 
                  style={{ background: '#ef4444', color: '#fff' }}
                  onClick={handleDeleteKey}
                >
                  <i className="fas fa-trash"></i> {t('dashboard.deleteKey')}
                </button>

                {!isPermanent && !isExpired && !isBlocked && licenseType !== 'demo' && (
                  <button 
                    className="cyber-action-btn" 
                    style={{ background: '#8b5cf6', color: '#fff' }}
                    onClick={onUpgradeToPermanent}
                  >
                    <i className="fas fa-arrow-up"></i> {t('dashboard.upgradeToPermanent')}
                  </button>
                )}

                {licenseType === 'demo' && !isBlocked && (
                  <>
                    <button
                      className="cyber-action-btn"
                      style={{ background: '#8b5cf6', color: '#fff' }}
                      onClick={() => onUpgradeDemo('monthly')}
                    >
                      <i className="fas fa-arrow-up"></i> {t('dashboard.upgradeToMonthly') || 'Upgrade to Monthly'}
                    </button>
                    <button
                      className="cyber-action-btn"
                      style={{ background: '#10b981', color: '#fff' }}
                      onClick={() => onUpgradeDemo('permanent')}
                    >
                      <i className="fas fa-arrow-up"></i> {t('dashboard.upgradeToPermanent')}
                    </button>
                  </>
                )}

                {!isPermanent && isExpired && !isBlocked && licenseType !== 'demo' && (
                  <button 
                    className="cyber-action-btn" 
                    style={{ background: '#f59e0b', color: '#fff' }}
                    onClick={handleExtendLicense}
                  >
                    <i className="fas fa-clock"></i> {t('dashboard.extendLicense')}
                  </button>
                )}

                {isBlocked && (
                  <span style={{ color: '#ef4444', fontSize: '14px', padding: '8px' }}>
                    <i className="fas fa-exclamation-triangle"></i> {t('dashboard.licenseBlockedContactSupport')}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ color: '#6b7280', marginBottom: '20px' }}>{t('dashboard.noLicenseMessage')}</p>
              <button className="cyber-action-btn" style={{ background: '#8b5cf6', color: '#fff' }} onClick={onBuyKeyClick}>
                <i className="fas fa-shopping-cart"></i> {t('dashboard.buyLicenseOrDemo')}
              </button>
            </div>
          )}
        </div>

        <div className="cyber-panel">
          <div className="panel-header">
            <h3>{t('dashboard.activeComponents')}</h3>
          </div>
          <div className="owned-modules-list">
            {modules.filter(m => m.owned).length > 0 ? (
              modules.filter(m => m.owned).map(mod => (
                <div key={mod.id} className="owned-module-item">
                  <span>
                    <i className="fas fa-check-circle" style={{ color: '#10b981', marginRight: '8px' }}></i> 
                    {getModuleName(mod.id)}
                  </span>
                  <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '800' }}>{t('dashboard.ready')}</span>
                </div>
              ))
            ) : (
              <p style={{ color: '#6b7280', fontSize: '13px', padding: '10px' }}>{t('dashboard.noActiveModules')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
